import { useMemo, useCallback } from "react"
import { useFlashOnChange } from "hooks/use-flash-on-change"
import { useOutletContext } from "react-router-dom"
import PropTypes from "prop-types"
import { map, chain, get, isEmpty, orderBy, some, first, pick, noop, isPlainObject } from "lodash"
import { Accordion, AccordionDetails, AccordionSummary, IconButton } from "@mui/material"
import { Add, ExpandLess, ExpandMore, Remove, InfoOutlined } from "@mui/icons-material"
import classNames from "classnames"
import OPTooltip from "components/material/tooltip"
import { handleGenerateHeaders, handleGenerateRows } from "connected-components/dashboard/components/download/helpers"
import { ERROR, LOADING, OK } from "helpers/data-statuses"
import { useRegisterDownloadData } from "hooks/use-register-download-data"
import { singleDateDataItemContract } from "gatherers/single-date/contract"
import useDownloadData from "../../hooks/download-data"
import CompareSectionStatus from "./loading"
import SkeletonSection from "../skeleton-section"
import SummaryValues from "./summary-values"
import { getMetricSignClass } from "helpers/metrics/utils"
import { buildScaledCurrencyLabel } from "helpers/metrics/currency-scale-label"
import { getCurrencyScaleForValues, makeScaledValueFormatter } from "helpers/formatters/currency-scale"

import "./style.scss"

const MAX_ROWS = 8

// Component to render a metric value with optional warning or value-level tooltip (e.g. Sharpe breakdown)
function MetricValue({ value, warning, valueFormatter, valueTooltip, isPerformanceMetric }) {
  if (value == null && warning) {
    return <OPTooltip title={warning}><span className="warning-char">⚠</span></OPTooltip>
  }
  const formatted = value == null ? "-" : valueFormatter(value)
  const metricSignClass = isPerformanceMetric ? getMetricSignClass(value) : ""
  const performanceValueClassName = isPerformanceMetric
    ? classNames("metric-value", metricSignClass)
    : null
  const valueContent = performanceValueClassName
    ? <span className={performanceValueClassName}>{formatted}</span>
    : formatted

  if (valueTooltip) {
    return (
      <OPTooltip title={valueTooltip}>
        <span className="compare-section-value-with-tooltip">
          <InfoOutlined fontSize="small" className="compare-section-value-tooltip-icon" aria-hidden />
          {valueContent}
        </span>
      </OPTooltip>
    )
  }
  return valueContent
}

MetricValue.propTypes = {
  value: PropTypes.any,
  warning: PropTypes.string,
  valueFormatter: PropTypes.func.isRequired,
  valueTooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  isPerformanceMetric: PropTypes.bool,
}

function ValueCell({ value, warning, valueFormatter, valueTooltip, isPerformanceMetric }) {
  const flashRef = useFlashOnChange(value)
  return (
    <div ref={flashRef} data-test-class="compare-section-row-value">
      <MetricValue value={value} warning={warning} valueFormatter={valueFormatter} valueTooltip={valueTooltip} isPerformanceMetric={isPerformanceMetric} />
    </div>
  )
}

ValueCell.propTypes = {
  value: PropTypes.any,
  warning: PropTypes.string,
  valueFormatter: PropTypes.func.isRequired,
  valueTooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  isPerformanceMetric: PropTypes.bool,
}

/**
 * Helper to check if a summary item has a truthy value.
 * Handles both "plain numbers" format and new "objects with value property" format
 * @param {number | object} summaryItem - Summary value (number or object with value property)
 * @returns {boolean} True if the summary has a truthy value
 */
function hasValue(summaryItem) {
  if (isPlainObject(summaryItem) && "value" in summaryItem) {
    return !!summaryItem.value
  }
  return !!summaryItem
}

function withCurrencyScale(row) {
  if (!row.isCurrency) return row
  const currencyScale = row.values.some(Number.isFinite) ? getCurrencyScaleForValues(row.values) : null
  return { ...row, currencyScale }
}

export default function CompareSection(props) {
  const { setDownloadData = noop } = useOutletContext()
  const {
    status,
    data,
    name,
    isGroup,
    formatter,
    configKey,
    color,
    defaultExpanded = false,
    sorted = false,
    columnCount,
    rowCount = MAX_ROWS,
    instrumentsWithMetadata,
    positionSetsMetadata,
    downloadName,
    index: sectionIndex,
    alwaysMultipleRows = false,
    queryState = { expanded: defaultExpanded, all: false },
    onQueryStateChange = noop,
    isEndVsStartDateComparison,
    useSkeletonLoader = false,
    buttonSize = "medium",
    tooltip,
    currency,
  } = props

  const rows = useMemo(() => chain(data)
    .reduce((accumulator, item, columnIndex) => {
      for (const id in item.values) {
        const cell = item.values[id]
        if (!accumulator[id]) {
          accumulator[id] = {
            id,
            ...pick(cell, [
              "label",
              "formatter",
              "tooltip",
              "tooltipPlacement",
              "isPerformanceMetric",
              "isCurrency",
              "currency",
            ]),
            values: Array(data.length).fill(null),
            warnings: Array(data.length).fill(undefined),
            valueTooltips: Array(data.length).fill(undefined),
          }
        }
        accumulator[id].values[columnIndex] = cell.value
        accumulator[id].warnings[columnIndex] = cell.warning
        if (cell.tooltipPlacement === "value" && cell.tooltip) {
          accumulator[id].valueTooltips[columnIndex] = cell.tooltip
        }
      }

      return accumulator
    }, {})
    .map(withCurrencyScale)
    .thru(rows => {
      if (!sorted) {
        return rows
      }

      return orderBy(rows, ({ values }) => {
        for (const value of values) {
          if (value) {
            return Math.abs(value)
          }
        }
      }, "desc")
    })
    .value(),
  [data, sorted])

  const generateHeaders = useCallback(() =>
    handleGenerateHeaders(instrumentsWithMetadata, positionSetsMetadata, "Metric", isEndVsStartDateComparison),
  [instrumentsWithMetadata, positionSetsMetadata, isEndVsStartDateComparison])

  const generateRows = useCallback(() =>
    handleGenerateRows(rows, formatter, currency),
  [rows, formatter, currency])

  const generatedDownloadName = useMemo(() => downloadName || `${name}${isGroup ? " Group" : ""}`, [name, isGroup, downloadName])

  useDownloadData({
    configKey,
    sectionIndex,
    name: generatedDownloadName,
    setDownloadData,
    generateHeaders,
    generateRows,
  })

  // Dashboard KPI downloads should be combined into a single sheet
  const downloadRegistration = useMemo(() => ({
    key: `kpi.${configKey}`,
    sheetName: "KPI",
    combineWithOthers: true,
    getData: () => ({ headers: generateHeaders(), rows: [[name], ...generateRows()] }),
  }), [configKey, name, generateHeaders, generateRows])
  useRegisterDownloadData(downloadRegistration)

  const summary = useMemo(() => map(data, item => get(item, "summary")), [data])
  const nameStyle = color ? { color } : null

  if (status === LOADING) {
    if (useSkeletonLoader) {
      return <SkeletonSection rowCount={rowCount} colCount={columnCount} />
    }
    return <CompareSectionStatus {...{
      status: "Loading...",
      expanded: queryState?.expanded,
      color,
      name,
      nameStyle,
      columnCount,
      rowCount,
    }}
    />
  }

  if (status === ERROR) {
    return <div>Error</div>
  }

  const headerTooltip = tooltip ? <OPTooltip title={tooltip}><InfoOutlined fontSize="small" /></OPTooltip> : null

  if (isEmpty(rows) && some(summary, hasValue)) {
    return <Accordion square className="renderers_compare-section single-row">
      <AccordionSummary className="header">
        <div style={{ backgroundColor: color }} />
        <div className="name" style={nameStyle}>
          <span>{name}</span>
        </div>
        <span>{headerTooltip}</span>
        <SummaryValues summary={summary} formatter={formatter} className="single-row-summary-value" />
      </AccordionSummary>
    </Accordion>
  }

  if (isEmpty(rows)) {
    return null
  }

  const showAllButton = rows.length > MAX_ROWS

  let adjustedName = name
  if (rows.length === 1 && !alwaysMultipleRows) {
    const firstRow = first(rows)
    if (name !== firstRow.label) {
      adjustedName = `${name} (${firstRow.label})`
    }

    return <Accordion
      square
      className="renderers_compare-section single-row"
      expanded={false}
    >
      <AccordionSummary className="header" data-test-class="compare-section-summary">
        <div style={{ backgroundColor: color }} />
        <div className="name" style={nameStyle}>{adjustedName}</div>
        <span>{headerTooltip}</span>
        {map(firstRow.values, (value, index) => (
          <ValueCell
            key={index}
            value={value}
            warning={firstRow.warnings[index]}
            valueFormatter={formatter}
            valueTooltip={firstRow.valueTooltips?.[index]}
            isPerformanceMetric={firstRow.isPerformanceMetric}
          />
        ))}
      </AccordionSummary>
    </Accordion>
  }

  return <Accordion
    square
    className={classNames("renderers_compare-section", { all: queryState?.all })}
    expanded={queryState?.expanded ?? defaultExpanded}
    onChange={(_, expanded) => { onQueryStateChange({ expanded }) }}
    data-test-class="compare-section-summary"
  >
    <AccordionSummary className="header" component="div">
      <div style={{ backgroundColor: color }} />
      <div className="name" data-test-class="compare-section-label" style={nameStyle}>
        <span>{adjustedName}</span>
        <span>{headerTooltip}</span>
        <IconButton size={buttonSize} className="expand-icon">
          {queryState?.expanded ? <ExpandLess fontSize="inherit" /> : <ExpandMore fontSize="inherit" />}
        </IconButton>
        {showAllButton && queryState?.expanded && <IconButton
          size={buttonSize}
          className="show-all-icon"
          onClick={e => {
            e.stopPropagation()
            onQueryStateChange({ all: !queryState?.all })
          }}
        >
          {queryState?.all ? <Remove fontSize="inherit" /> : <Add fontSize="inherit" />}
        </IconButton>}
      </div>
      <SummaryValues summary={summary} formatter={formatter} />
    </AccordionSummary>
    <AccordionDetails className="body">
      {map(rows, (row, id) => {
        const labelTooltip = row.tooltip && row.tooltipPlacement !== "value" ? (
          <OPTooltip title={row.tooltip}>
            <InfoOutlined fontSize="small" />
          </OPTooltip>
        ) : null
        const displayLabel = buildScaledCurrencyLabel(row.label, row.currencyScale, row.currency)
        const displayFormatter = row.currencyScale ? makeScaledValueFormatter(row.currencyScale) : row.formatter ?? formatter
        return <div key={id} data-test-class="compare-section-row">
          <div style={{ backgroundColor: color }} />
          <div className="tooltip-label-wrapper">
            <span className="tooltip-label">{displayLabel}</span>
            <span>{labelTooltip}</span>
          </div>
          {map(row.values, (value, index) => (
            <ValueCell
              key={index}
              value={value}
              warning={row.warnings[index]}
              valueFormatter={displayFormatter}
              valueTooltip={row.valueTooltips?.[index]}
              isPerformanceMetric={row.isPerformanceMetric}
            />
          ))}
        </div>
      },
      )}
    </AccordionDetails>
  </Accordion>
}

CompareSection.propTypes = {
  configKey: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  status: PropTypes.oneOf([OK, ERROR, LOADING]).isRequired,
  data: PropTypes.arrayOf(singleDateDataItemContract),
  formatter: PropTypes.func.isRequired,
  color: PropTypes.string,
  defaultExpanded: PropTypes.bool,
  sorted: PropTypes.bool,
  columnCount: PropTypes.number.isRequired,
  rowCount: PropTypes.number,
  isGroup: PropTypes.bool,
  positionSetsMetadata: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  instrumentsWithMetadata: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  downloadName: PropTypes.string,
  index: PropTypes.number.isRequired,
  alwaysMultipleRows: PropTypes.bool,
  queryState: PropTypes.shape({
    expanded: PropTypes.bool,
    all: PropTypes.bool,
  }),
  onQueryStateChange: PropTypes.func,
  isEndVsStartDateComparison: PropTypes.bool,
  useSkeletonLoader: PropTypes.bool,
  buttonSize: PropTypes.string,
  tooltip: PropTypes.string,
  currency: PropTypes.string,
}
